import { type QueryFunctionContext, useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import { makePickSavingField } from '@helpers/makePickSavingField';

import { apiClient } from '@services/apiClient';

import {
  type ICostSavingsData,
  type ITotalCostSavingsKeys,
  type ITotalCostSavingsYearChartStats,
  type ITotalCostSavingsYearStats,
} from '../model/totalCostSavings';

const parseCostSaving = (value: string) => parseFloat(value.replaceAll(',', ''));

const mapYearStats = (locationIds: Array<number>, stats?: ITotalCostSavingsYearStats) => {
  if (!(stats && stats.byMonth && stats.byMonth?.length)) return null;

  const pick = makePickSavingField(!locationIds);

  const byMonthPotentialCostSavings: ICostSavingsData[] = [];

  const byMonthCostSavings: ICostSavingsData[] = [];

  const byMonthPotentialDowntimeAvoided: ICostSavingsData[] = [];

  const byMonthDowntimeAvoided: ICostSavingsData[] = [];

  stats.byMonth.forEach((item) => {
    const parsedPotentialCostSavings = parseCostSaving(
      pick(item.potentialCostSavingsCustomerCurrency, item.potentialCostSavingsFacilityCurrency),
    );
    const parsedCostSavings = parseCostSaving(
      pick(item.verifiedCostSavingsCustomerCurrency, item.verifiedCostSavingsFacilityCurrency),
    );

    const parsedPotentialDowntimeAvoided = parseFloat(item.potentialDowntimeAvoided);
    const parsedDowntimeAvoided = parseFloat(item.verifiedDowntimeAvoided);

    byMonthPotentialCostSavings.push({
      y: parsedPotentialCostSavings,
      custom: {
        realValue: pick(item.potentialCostSavingsCustomerCurrency, item.potentialCostSavingsFacilityCurrency),
      },
      name: item.monthName,
    });

    byMonthCostSavings.push({
      y: parsedCostSavings,
      custom: {
        realValue: pick(item.verifiedCostSavingsCustomerCurrency, item.verifiedCostSavingsFacilityCurrency),
      },
      name: item.monthName,
    });

    byMonthPotentialDowntimeAvoided.push({
      y: parsedPotentialDowntimeAvoided,
      custom: { realValue: item.potentialDowntimeAvoided },
      name: item.monthName,
    });

    byMonthDowntimeAvoided.push({
      y: parsedDowntimeAvoided,
      custom: { realValue: item.verifiedDowntimeAvoided },
      name: item.monthName,
    });
  });

  return {
    potentialCostSavings: pick(
      stats.potentialCostSavingsCustomerCurrency,
      stats.potentialCostSavingsFacilityCurrency,
    ),
    ytdSavings: pick(stats.ytdSavingsCustomerCurrency, stats.ytdSavingsFacilityCurrency),
    potentialDowntimeAvoided: stats.potentialDowntimeAvoided,
    verifiedDowntimeAvoided: stats.verifiedDowntimeAvoided,
    totalDowntimeAvoided: stats.totalDowntimeAvoided,
    byMonthPotentialCostSavings,
    byMonthCostSavings,
    byMonthPotentialDowntimeAvoided,
    byMonthDowntimeAvoided,
  };
};

const totalCostSavingsKey = (locationIds: Array<number>, year: string) => [
  'totalCostSavings',
  locationIds,
  year,
];

const totalCostSavingsFn = ({ queryKey }: QueryFunctionContext<string[]>) => {
  return apiClient.get<{ data: ITotalCostSavingsYearStats }>(
    `${APP_API_URL_V2}fsd/action-item/executive-summary/cost-savings`,
    {
      params: {
        location_ids: queryKey[1],
        year: queryKey[2],
      },
    },
  );
};

const totalCostSavingsSelect =
  (locationIds: Array<number>) =>
  ({ data }: AxiosResponse<{ data: ITotalCostSavingsYearStats }>): ITotalCostSavingsYearChartStats | null =>
    mapYearStats(locationIds, data.data);

export const useTotalCostSavingsQuery = ({ locationIds, year }: ITotalCostSavingsKeys) => {
  return useQuery({
    // @ts-ignore TODO: update react-query to v5
    queryKey: totalCostSavingsKey(locationIds, year),
    queryFn: totalCostSavingsFn,
    select: totalCostSavingsSelect(locationIds),
  });
};
